/**
 * /wireline-slickline — the vertical's landing page (WL-1, fieldiq-api#1336).
 *
 * Light on purpose: a way into each register, and the lines that need a look.
 * "Needs a look" is a FILTER on the API's own folded values — a pull test or
 * inspection the API marks overdue, failed or never tested, or an event the
 * API says does not fold. No date or length is computed here.
 *
 * The register counts are the number of rows each list returned; a failed
 * read shows `—`, never 0.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cable, MapPin, Truck, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlLines, useWlToolStrings, useWlUnits, useWlWells } from '../../hooks/useWirelineSlickline';
import { queryLoadFailure } from '../../lib/load-failure';
import { WL_ATTENTION_STATUSES, wlDueSkin, wlLineLength } from '../../lib/wireline-slickline';
import type { WlLine } from '../../types/wireline-slickline';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WlBadge, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

/** The API's values, filtered. Nothing derived. */
export const needsALook = (l: WlLine): boolean =>
  !l.state?.retired &&
  (WL_ATTENTION_STATUSES.includes(l.state?.pullTest?.status ?? '') ||
    WL_ATTENTION_STATUSES.includes(l.state?.inspection?.status ?? '') ||
    (l.state?.issues?.length ?? 0) > 0);

export default function WlOverviewScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const wells = useWlWells();
  const units = useWlUnits();
  const lines = useWlLines();
  const strings = useWlToolStrings();
  const countOf = (q: { isLoading: boolean; error?: unknown; fetchStatus?: string; failureReason?: unknown; data?: { data: unknown[] } }) =>
    q.isLoading || queryLoadFailure(q) ? undefined : q.data?.data.length;
  const lineRows = lines.data?.data ?? [];
  const attention = lineRows.filter(needsALook);

  const columns: WlColumn<WlLine>[] = [
    { key: 'line', header: 'Line', render: (l) => <p className="font-extrabold text-white">{l.lineNumber || '—'}</p> },
    { key: 'type', header: 'Type', render: (l) => l.lineTypeLabel ?? l.lineType ?? '—' },
    { key: 'drum', header: 'On drum', align: 'right', render: (l) => <span className="font-mono text-sm">{wlLineLength(l.state?.lengthOnDrum, l.state?.lengthUnit)}</span> },
    { key: 'pull', header: 'Pull test', render: (l) => <WlBadge skin={wlDueSkin(l.state?.pullTest?.status)} value={l.state?.pullTest?.status} /> },
    { key: 'inspection', header: 'Inspection', render: (l) => <WlBadge skin={wlDueSkin(l.state?.inspection?.status)} value={l.state?.inspection?.status} /> },
    { key: 'issues', header: 'Issues', align: 'right', render: (l) => <span className="font-mono text-sm">{displayValue(l.state?.issues?.length)}</span> },
  ];

  const cards = [
    { to: '/wireline-slickline/wells', label: 'Wells', icon: MapPin, q: wells, testId: 'wl-overview-wells' },
    { to: '/wireline-slickline/units', label: 'Units', icon: Truck, q: units, testId: 'wl-overview-units' },
    { to: '/wireline-slickline/lines', label: 'Lines', icon: Cable, q: lines, testId: 'wl-overview-lines' },
    { to: '/wireline-slickline/tool-strings', label: 'Tool strings', icon: Wrench, q: strings, testId: 'wl-overview-tool-strings' },
  ];

  return (
    <div data-testid="wl-overview-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Wireline & Slickline"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Cable className="size-4" aria-hidden="true" />, label: 'Lines', tone: 'neutral', loading: lines.isLoading, value: displayValue(countOf(lines)) },
          {
            icon: <Cable className="size-4" aria-hidden="true" />,
            label: 'Needs a look',
            sub: 'Tests or issues',
            tone: 'accent',
            loading: lines.isLoading,
            value: displayValue(lines.isLoading || queryLoadFailure(lines) ? undefined : attention.length),
          },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={c.testId}
                className="flex items-center justify-between rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 hover:border-fi-amber"
              >
                <span className="inline-flex items-center gap-2 font-extrabold text-white">
                  <c.icon className="size-4" aria-hidden="true" />
                  {c.label}
                </span>
                <span className="font-mono text-lg text-fieldiq-text">{c.q.isLoading ? '…' : displayValue(countOf(c.q))}</span>
              </Link>
            ))}
          </div>
          <WlPanel eyebrow="From each line's folded state" title="Lines that need a look" testId="wl-overview-attention">
            <WlRegister
              query={lines}
              rows={attention}
              columns={columns}
              rail={(l) => wlDueSkin(l.state?.pullTest?.status).rail}
              onOpen={(l) => openRecord(`/wireline-slickline/lines/${l.id}`)}
              subject="lines"
              testId="wl-attention-line"
              minWidth="min-w-[720px]"
              empty={<WlEmpty>No line is overdue, failed, untested or carrying an issue.</WlEmpty>}
              card={(l) => (
                <>
                  <p className="text-sm font-extrabold text-white">{l.lineNumber || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    Pull test {wlDueSkin(l.state?.pullTest?.status).label} · Inspection {wlDueSkin(l.state?.inspection?.status).label}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
